import {useApi} from "@/composables/useApi";
import type {CartData, CartItemData, NewOrderResponse} from "@/types/ecommerce";
import {useErrorModal} from "@/composables/useErrorModal";
import {useWarningModal} from "@/composables/useWarningModal";

const { setErrors } = useErrorModal();
const { setWarning } = useWarningModal();
const { $toast } = useNuxtApp();

export const useCart = async () => {

    const cart = useState<CartData | null>('cart', () => null);
    const fetchingCart = useState('fetchingCart', () => false);
    const lastCartItemsResponse = useState<CartItemData[]>('lastCartItemsResponse', () => []);
    const cartItems = useState<CartItemData[]>('cartItems', () => []);
    const lastCompletedCart = useState<NewOrderResponse|null>('lastCompletedCart', () => null);
    const completingCart = useState<boolean>('completingCart', () => false);

    const fetchCart = async () => {
        fetchingCart.value = true;
        const { fetch, data: cartData } = useApi<CartData>({
            url: 'active-carts',
            method: 'GET',
        });
        await fetch();
        cart.value = cartData.value;
        if(cart.value) {
            const { fetch: fetchCartItems, data: cartItemData } = useApi<CartItemData[]>({
                url: `carts/${cart.value.id}/items`,
                method: 'GET',
                query: {
                    with_main_image: true
                }
            });
            await fetchCartItems();
            if(cartItemData.value) {
                lastCartItemsResponse.value = JSON.parse(JSON.stringify(cartItemData.value));
                cartItems.value = cartItemData.value;
            }
        }
        setTimeout(() => {
            fetchingCart.value = false;
        }, 2000)
    }

    const deleteCart = async () => {
        if(cart.value) {
            const { fetch } = useApi<null>({
                url: `carts/${cart.value?.id}`,
                method: 'DELETE',
            });
            await fetch();
            cart.value = null;
            cartItems.value = [];
        }
    }

    const createCart = async (location_id: number, channel_id: number, order_type_id: number) => {
        const { fetch: postCart, error } = useApi<CartData>({
            url: `carts`,
            method: 'POST',
            payload: {
                channel_id: channel_id,
                location_id: location_id,
                order_type_id: order_type_id,
            }
        });

        await postCart();

        if(error.value) {
            $toast.error(error.value.message);
            return;
        }

        await fetchCart();
    }

    const getCartItem = (productId: number) => {
        return cartItems.value.find(i => i.product_id === productId);
    }

    const addCartItem = async (productId: number, quantity: number) => {
        const { fetch, data, error } = useApi<CartItemData>({
            url: `carts/${cart.value?.id}/items`,
            method: 'POST',
            payload: {
                product_id: productId,
                quantity: quantity,
            }
        });
        await fetch();
        await fetchCart();
    }

    const updateCartItem = async(productId: number, quantity: number, fetchCartAfter: boolean = true) => {
        let item = cartItems.value.find((i) => {
            return i.product_id === productId;
        });
        if(!item) {
            await fetchCart();
            $toast.error('Cart item not found, please try again');
        }
        const { fetch, data, error } = useApi<CartItemData[]>({
            url: `carts/${cart.value?.id}/items/${item?.id}`,
            method: 'PATCH',
            payload: {
                product_id: productId,
                quantity: quantity,
            }
        });
        await fetch();
        if(fetchCartAfter) {
            await fetchCart();
        }
    }

    const updateCart = async () => {
        const promises = changedCartItems.value.map((item) => {
            return updateCartItem(item.product_id, item.quantity, false);
        });

        await Promise.all(promises);

        await fetchCart();
    }

    const removeCartItem = async(productId: number) => {
        let cartItem = getCartItem(productId);
        if(!cartItem) {
            $toast.error('Cart item not found, please try again');
            return;
        }
        const { fetch, data, error } = useApi<null>({
            url: `carts/${cart.value?.id}/items/${cartItem.id}`,
            method: 'DELETE',
        });

        await fetch();
        await fetchCart();
    }

    const completeCart = async (payload: object) => {
        // Guard against duplicate submissions (e.g. a double-click, or both checkout
        // modals firing) which would send more than one complete request and create
        // duplicate orders. The flag is set synchronously before the request, so a
        // second call in the same tick returns here before it can fetch;
        if(completingCart.value) {
            return;
        }
        completingCart.value = true;

        const { fetch, data, error } = useApi<NewOrderResponse>({
            url: `carts/${cart.value?.id}/complete`,
            method: 'POST',
            payload: payload,
        });

        try {
            await fetch();

            if(error.value) {
                setErrors(
                    error.value?.bag,
                    "We're sorry, something went wrong while trying to complete your order",
                    async () => { await completeCart(payload) }
                );
                return;
            }

            if(data.value) {
                if(data.value.heldOrderMessage) {
                    setWarning(
                        data.value.heldOrderMessage,
                        data.value.statusMessage,
                        () => {
                            if(data.value) {
                                afterCartComplete(data.value);
                            }
                        }
                    )

                    return;
                }
            }

            if(data.value) {
                afterCartComplete(data.value);
            }
        } finally {
            completingCart.value = false;
        }
    }
    
    const afterCartComplete = (response: NewOrderResponse) => {
        lastCompletedCart.value = response;
        cart.value = null;
        cartItems.value = [];
        lastCartItemsResponse.value = [];
        navigateTo('/order-confirmed');
    }

    const changedCartItems = computed(() => {
         return cartItems.value.filter((ci) => {
            return lastCartItemsResponse.value.find(lci => ci.id === lci.id)?.quantity !== ci.quantity;
        });
    });

    if(!cart.value && !fetchingCart.value) {
        await fetchCart();
    }

    return {
        cart,
        cartItems,
        fetchingCart,
        completingCart,
        changedCartItems,
        lastCartItemsResponse,
        lastCompletedCart,
        createCart,
        fetchCart,
        updateCart,
        getCartItem,
        deleteCart,
        addCartItem,
        updateCartItem,
        removeCartItem,
        completeCart
    }
}